import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { CreditCard, Crown, Users, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentTestPage = () => {
  const [loading, setLoading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState('member');
  const navigate = useNavigate();

  const packages = {
    member: {
      title: "Success Club Membership",
      price: "$15/month",
      icon: <Users className="w-8 h-8 text-blue-500" />,
      features: [
        "Access to exclusive business network",
        "Monthly Success Club magazine included",
        "Event attendance and networking opportunities", 
        "Challenge participation and point system",
        "Community chat and messaging"
      ]
    }
  };

  const handlePayment = async () => {
    setLoading(true);
    try {
      const origin = window.location.origin;
      
      // Send as query parameters instead of form data
      const response = await axios.post(
        `${API}/payments/checkout?package_id=member&origin_url=${encodeURIComponent(origin)}`,
        {},
        {
          headers: {
            'Origin': origin
          }
        }
      );
      
      // Store session info for tracking
      localStorage.setItem('checkout_session_id', response.data.session_id);
      localStorage.setItem('test_package', 'member');
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
      
    } catch (error) {
      console.error('Payment error:', error);
      alert('Failed to create payment session. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_refined-login/artifacts/7hacqtlv_Success%20Club%20Logo%20Files%20-%203%20%281%29.png" 
                alt="Success Club Logo" 
                className="w-full h-full object-contain"
              />
            </div>
          </div>
          <h1 className="text-3xl font-bold mb-2" style={{fontFamily: "'Playfair Display', serif"}}>
            Payment System Test
          </h1>
          <p className="text-gray-300">Test the Stripe integration and payment flow</p>
        </div>

        {/* Package Display */}
        <div className="flex justify-center mb-8">
          <Card className="bg-gray-800 border-amber-500 border-2 max-w-md">
            <CardHeader className="text-center">
              <div className="flex items-center justify-center mb-4">
                <Users className="w-8 h-8 text-blue-500" />
              </div>
              <CardTitle className="text-white text-xl">Success Club Membership</CardTitle>
              <div className="text-2xl font-bold text-amber-500">$15/month</div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {packages.member.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span className="text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Payment Button */}
        <div className="text-center mb-8">
          <Button
            onClick={handlePayment}
            disabled={loading}
            className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 text-lg"
          >
            <CreditCard className="w-5 h-5 mr-2" />
            {loading ? 'Processing...' : 'Pay $15/month with Stripe'}
          </Button>
        </div>

        {/* Test Instructions */}
        <Card className="bg-gray-900 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">🧪 Test Instructions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 text-gray-300">
              <div>
                <h4 className="text-white font-semibold mb-2">Test Credit Card Numbers:</h4>
                <div className="bg-gray-800 p-3 rounded font-mono text-sm">
                  <div>✅ Success: 4242 4242 4242 4242</div>
                  <div>❌ Decline: 4000 0000 0000 0002</div>
                  <div>⚠️ Require Auth: 4000 0025 0000 3155</div>
                </div>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-2">Test Details:</h4>
                <ul className="list-disc list-inside text-sm space-y-1">
                  <li>Use any future expiry date (e.g., 12/25)</li>
                  <li>Use any 3-digit CVV (e.g., 123)</li>
                  <li>Use any ZIP code (e.g., 12345)</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="text-center mt-8 space-y-4">
          <div>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-800 mr-4"
            >
              Back to Home
            </Button>
            <Button
              onClick={() => navigate('/portal/member')}
              variant="outline" 
              className="border-blue-500 text-blue-400 hover:bg-blue-500/10 mr-4"
            >
              Test Member Portal
            </Button>
            <Button
              onClick={() => navigate('/portal/host')}
              variant="outline"
              className="border-amber-500 text-amber-400 hover:bg-amber-500/10"
            >
              Test Host Portal
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentTestPage;